import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/authProvider';
import { Card, CardContent, Button, Input, Alert } from '@license-management/ui';
import { LogIn, AlertCircle, Shield } from 'lucide-react';

interface LocationState {
  from?: { pathname: string };
}

/**
 * Sanitize redirect path to prevent open redirect attacks.
 * Only allows relative paths that start with a single slash.
 */
function getSafeRedirectPath(pathname: string | undefined): string {
  const defaultPath = '/';
  if (!pathname) return defaultPath;
  // Only allow paths starting with single slash, no protocol, no double slash
  if (pathname.startsWith('/') && !pathname.startsWith('//') && !pathname.includes('://')) {
    return pathname;
  }
  return defaultPath;
}

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      const state = location.state as LocationState;
      const redirectPath = getSafeRedirectPath(state?.from?.pathname);
      navigate(redirectPath, { replace: true });
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Account is deactivated. Please contact support.') {
          setError('Your account has been deactivated. Please contact support for assistance.');
        } else {
          setError(error.message);
        }
      } else {
        setError('An error occurred during login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1628] flex flex-col items-center justify-center p-4">
      {/* Subtle background — fine dot grid over deep navy */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(90,145,196,0.3) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="absolute top-1/4 -left-20 w-96 h-96 bg-primary-600/15 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-80 h-80 bg-primary-800/20 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo and title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-primary-400 to-primary-700 text-white mb-5 shadow-glow-primary">
            <Shield className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1 tracking-tight">NGA Admin</h1>
          <p className="text-[#4a6a8a] text-sm tracking-wide uppercase" style={{ letterSpacing: '0.1em' }}>
            License Management System
          </p>
        </div>

        {/* Login card */}
        <Card shadow="elevated" className="animate-fade-in border border-[#1e3a5f]/60">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <Alert variant="error" icon={<AlertCircle className="h-5 w-5" />}>
                  {error}
                </Alert>
              )}

              <Input
                label="Email address"
                type="email"
                id="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
              />

              <Input
                label="Password"
                type="password"
                id="password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
              />

              <Button
                type="submit"
                loading={isLoading}
                className="w-full"
                size="lg"
                leftIcon={!isLoading ? <LogIn className="h-4 w-4" /> : undefined}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-[#4a6a8a] tracking-widest uppercase">
          Nordic Graphic Aid &middot; Internal Use Only
        </p>
      </div>
    </div>
  );
};

export default Login;
